import { TextValidatorType } from "anketa-core/base";
import { Group, Section } from "anketa-core/group";
import { Translation } from "anketa-core/i18n";
import { OpenQuestion } from "anketa-core/open_question";
import { OptionsQuestion } from "anketa-core/options_question";
import { TextValidator } from "anketa-core/validation";

export const setupNetboxGroup = (netboxScanners: Section, idx = 0) => {
  const netboxGroup = netboxScanner(idx);
  netboxScanners.addChild(netboxGroup.netboxScanner);

  function onConfigChange() {
    const name = netboxGroup.netboxName.fact.raw;

    if (name && name.toString().trim() !== "") {
      netboxGroup.netboxName.removeChangeListener(onConfigChange);
      setupNetboxGroup(netboxScanners, idx + 1);
    }
  }

  netboxGroup.netboxName.addChangeListener(onConfigChange);
};

export const netboxScanner = (
  idx: number
): { netboxScanner: Group; netboxName: OpenQuestion } => {
  const idPrefix = `netbox_scanner_${idx}`;

  const netboxScanner = new Group(idPrefix, [
    new Translation("en", "Unconfigured NetBox Scanner"),
    new Translation("de", "Unkonfigurierter NetBox Scanner"),
  ]);

  const name = new OpenQuestion(`${idPrefix}_name`, [
    new Translation("en", "Display name"),
    new Translation("de", "Anzeigename"),
  ]);
  name.validator = new TextValidator();

  name.required = true;
  name.addChangeListener(() => {
    const plainName = name.fact.raw.toString();
    if (plainName.trim() !== "") {
      netboxScanner.title = [
        new Translation("en", plainName),
        new Translation("de", plainName),
      ];
    } else {
      netboxScanner.title = [
        new Translation("en", "Unconfigured NetBox Scanner"),
        new Translation("de", "Unkonfigurierter NetBox Scanner"),
      ];
    }
  });
  netboxScanner.addChild(name);

  const netboxSearchDomain = new OpenQuestion(`${idPrefix}_search_domain`, [
    new Translation("en", "Search Domain"),
    new Translation("de", "Suchdomäne"),
  ]);
  netboxSearchDomain.validator = new TextValidator();

  netboxSearchDomain.help = [
    new Translation(
      "en",
      "The domain to use for device name lookups or filtering (optional)."
    ),
    new Translation(
      "de",
      "Die Domäne, die für Gerätesuchen oder Filterung verwendet wird (optional)."
    ),
  ];
  netboxScanner.addChild(netboxSearchDomain);

  const netboxUrl = new OpenQuestion(`${idPrefix}_url`, [
    new Translation("en", "NetBox API URL"),
    new Translation("de", "NetBox API-URL"),
  ]);
  netboxUrl.validator = new TextValidator(TextValidatorType.Uri);

  netboxUrl.required = true;
  netboxUrl.help = [
    new Translation(
      "en",
      "The full URL to your NetBox instance's API (e.g., https://netbox.example.com/api/)."
    ),
    new Translation(
      "de",
      "Die vollständige URL zur API Ihrer NetBox-Instanz (z.B. https://netbox.beispiel.de/api/)."
    ),
  ];
  netboxScanner.addChild(netboxUrl);

  const netboxToken = new OpenQuestion(`${idPrefix}_token`, [
    new Translation("en", "API Token"),
    new Translation("de", "API-Token"),
  ]);

  netboxToken.required = true;
  netboxToken.readonly = true;
  netboxToken.help = [
    new Translation(
      "en",
      "A NetBox API token with read-only access to devices."
    ),
    new Translation(
      "de",
      "Ein NetBox API-Token mit Nur-Lesen-Zugriff auf Geräte."
    ),
  ];
  netboxScanner.addChild(netboxToken);

  const netboxInsecure = new OptionsQuestion(
    `${idPrefix}_insecure`,
    [
      new Translation("en", "Disable Certificate Verification"),
      new Translation("de", "Zertifikatsüberprüfung deaktivieren"),
    ],
    "default.options.yes_no"
  );
  netboxInsecure.help = [
    new Translation(
      "en",
      "Should SSL/TLS certificate verification be disabled? Only use this for testing with self-signed certificates."
    ),
    new Translation(
      "de",
      "Soll die Überprüfung von SSL/TLS-Zertifikaten deaktiviert werden? Nur für Tests mit selbstsignierten Zertifikaten verwenden."
    ),
  ];
  if (netboxInsecure.fact.raw === null) {
    netboxInsecure.setFactValue(1);
  }
  netboxScanner.addChild(netboxInsecure);

  const netboxParallelism = new OptionsQuestion(
    `${idPrefix}_parallelism`,
    [
      new Translation("en", "Parallelism"),
      new Translation("de", "Parallelität"),
    ],
    "default.options.yes_no"
  );
  netboxParallelism.help = [
    new Translation(
      "en",
      "The number of concurrent API requests allowed (optional, advanced)."
    ),
    new Translation(
      "de",
      "Die Anzahl gleichzeitiger API-Anfragen (optional, fortgeschritten)."
    ),
  ];
  if (netboxParallelism.fact.raw === null) {
    netboxParallelism.setFactValue(1);
  }
  netboxScanner.addChild(netboxParallelism);

  return { netboxScanner, netboxName: name };
};
